import React from 'react'
import PropTypes from 'prop-types'
import { get } from 'lodash'
import classNames from 'classnames'

import ErrorMessage from './ErrorMessage'
import { useFormContext } from '@context/FormContext'

const PRIMARY_GREEN = '#99c543'

const Options = ({ name, autocomplete = 'off', selectedColor, ...props }) => {
  const { values, errors, submitCount, setFieldValue, setFieldTouched, setFieldError } = useFormContext()
  
  const fieldValue = get(values, name)
  const fieldError = get(errors, name)
  const fieldTouched = submitCount > 0
  const errorAndTouched = fieldError && fieldTouched
  const validAndTouched = !fieldError && fieldTouched
  const useGreenSelected = selectedColor === 'green'

  const handleChange = (e) => {
    const newValue = e.target.value
    setFieldTouched(name, true)
    setFieldValue(name, newValue)
    setFieldError(name, undefined)
    props.onChange && props.onChange(newValue)
  }

  const handleDoubleClick = () => {
    // Note: handleSubmit is not available in FormContext
    // This functionality may need to be handled differently
  }

  const isSquareGrid = props.variant === 'square' && props.maxColumns === 2

  return (
    <fieldset
      className={classNames(
        'mb-10 relative last:mb-0',
        { 'border-danger': errorAndTouched },
        { 'border-success': validAndTouched }
      )}
    >
      <legend
        className={classNames(
          'block mb-3 text-base font-bold text-gray-900 select-none md:mr-6',
          { 'absolute left-4 top-4 opacity-0 -z-10': !props.label }
        )}
      >
        {props.label || name}
      </legend>

      <div
        className={classNames(
          props.variant === 'square' && !isSquareGrid && 'md:flex md:flex-row md:flex-wrap md:-m-2',
          isSquareGrid && 'grid grid-cols-2 gap-3 max-w-md mx-auto'
        )}
      >
        {props.options.map((option) => (
          <div
            key={option.value}
            className={classNames(
              'relative',
              props.variant === 'rectangle' && 'mb-4 last:mb-0',
              props.variant === 'square' && !isSquareGrid && 'md:flex-1 md:max-w-[50%] md:p-2 md:mb-0',
              isSquareGrid && 'min-h-0',
              { 'bg-success': fieldValue === option.value },
              { 'opacity-50 cursor-not-allowed': props.disabled || option.disabled }
            )}
          >
              <input
                autoComplete={autocomplete}
                checked={String(fieldValue) === String(option.value)}
                className="sr-only"
                disabled={props.disabled || option.disabled}
                id={`${name}_${option.value}`}
                name={name}
                onChange={handleChange}
                type="radio"
                value={option.value}
              />

            <label
              className={classNames(
                'border-2 border-gray-200 rounded-lg cursor-pointer block h-full p-5 relative select-none transition-all duration-200',
                !isSquareGrid && 'bg-white hover:border-primary-orange hover:shadow-md hover:-translate-y-0.5',
                'focus-within:border-primary-orange focus-within:ring-2 focus-within:ring-primary-orange/20',
                isSquareGrid && 'group aspect-square flex flex-col items-center justify-center text-center p-3',
                isSquareGrid && fieldValue !== option.value && 'bg-gray-100 hover:bg-gray-300 hover:text-white hover:font-bold',
                fieldValue === option.value && !useGreenSelected && !isSquareGrid && 'bg-success border-success text-white shadow-md',
                fieldValue === option.value && !useGreenSelected && isSquareGrid && 'bg-[rgb(178.5,211.5,114)] border-[rgb(178.5,211.5,114)] text-white shadow-md',
                { 'bg-gray-100 border-gray-300 cursor-not-allowed transform-none opacity-60': props.disabled || option.disabled },
                props.variant === 'rectangle' && 'md:flex md:flex-row md:items-center bg-white'
              )}
              style={fieldValue === option.value && useGreenSelected ? { backgroundColor: PRIMARY_GREEN, borderColor: PRIMARY_GREEN, color: 'white' } : undefined}
              htmlFor={`${name}_${option.value}`}
              onDoubleClick={handleDoubleClick}
            >
              {option.icon && (
                <div className={classNames(
                  'flex items-center justify-center flex-shrink-0',
                  props.variant === 'rectangle' && 'h-16 md:flex-basis-16 md:mr-4 md:max-w-16 md:text-center md:mb-0 mb-4',
                  isSquareGrid && 'h-12 w-12 mb-1 [&_svg]:w-full [&_svg]:h-full',
                  isSquareGrid && fieldValue === option.value && '[&_svg]:fill-white [&_svg]:stroke-white',
                  isSquareGrid && fieldValue !== option.value && 'group-hover:[&_svg]:fill-white group-hover:[&_svg]:stroke-white'
                )}>
                  {option.icon}
                </div>
              )}

              <div className={classNames(props.variant === 'rectangle' && 'md:flex-1', isSquareGrid && 'flex flex-col items-center justify-center')}>
                {option.label && (
                  <span className={classNames(
                    'block w-full text-lg font-semibold',
                    fieldValue === option.value ? 'text-white' : 'text-gray-900',
                    isSquareGrid && fieldValue === option.value && 'font-bold'
                  )}>
                    {option.label}
                  </span>
                )}

                {option.description && (
                  <span className={classNames(
                    'block w-full text-sm mt-1',
                    fieldValue === option.value ? 'text-white/90' : 'text-gray-700',
                    isSquareGrid && fieldValue === option.value && 'font-bold'
                  )}>
                    {option.description}
                  </span>
                )}
              </div>
            </label>
          </div>
        ))}
      </div>

      <ErrorMessage
        centered={props.centered}
        message={fieldError}
        render={errorAndTouched}
      />
    </fieldset>
  )
}

// prettier-ignore
Options.propTypes = {
  autocomplete: PropTypes.string,
  centered: PropTypes.bool,
  disabled: PropTypes.bool,
  inline: PropTypes.bool,
  label: PropTypes.string,
  maxColumns: PropTypes.oneOf([2, 3, 4, 5, 6]),
  onChange: PropTypes.func,
  selectedColor: PropTypes.oneOf(['green']),
  variant: PropTypes.oneOf(['rectangle', 'square']),
  options: PropTypes.arrayOf(PropTypes.shape({
    description: PropTypes.string,
    disabled: PropTypes.bool,
    icon: PropTypes.node,
    label: PropTypes.string,
    value: PropTypes.any.isRequired,
  })).isRequired,
  name: PropTypes.string.isRequired,
}

export default Options
